import {
    IDocumentTable,
    ITableColumn,
    ITableColumnAbook,
    ITableColumnAutoComplete,
    ITableColumnCalc,
    ITableColumnDict,
    ITableColumnUrl,
    TableColumn,
} from '@models/Forms/IForms';
import { FormulaManager } from '@utils/managers/FormulaManager';
import { IFieldElem } from '@models/IFormData';
import dxDataGrid from 'devextreme/ui/data_grid';
import { sendNotification } from '@utils/notifications';
import { parseDisplayFormatCell } from '@utils/documentUtils';

export const isTableColumn = (anyObject: any): anyObject is ITableColumn => {
    return (
        (anyObject as ITableColumn) !== null &&
        (anyObject as ITableColumnDict).dictName === undefined &&
        (anyObject as ITableColumnCalc).formula === undefined &&
        (anyObject as ITableColumnAutoComplete).dataSource == null &&
        (anyObject as ITableColumnAbook).tabs === undefined &&
        (anyObject as ITableColumnUrl).href === undefined
    );
};
export const isTableColumnDict = (anyObject: any): anyObject is ITableColumnDict => {
    return (anyObject as ITableColumnDict).dictName !== undefined;
};
export const isTableColumnCalc = (anyObject: any): anyObject is ITableColumnCalc => {
    return (anyObject as ITableColumnCalc).formula !== undefined;
};
export const isTableColumnAutoComplete = (anyObject: any): anyObject is ITableColumnAutoComplete => {
    return (anyObject as ITableColumnAutoComplete).dataSource != null;
};
export const isTableColumnAbook = (anyObject: any): anyObject is ITableColumnAbook => {
    return (anyObject as ITableColumnAbook).tabs !== undefined;
};
export const isTableColumnUrl = (anyObject: any): anyObject is ITableColumnUrl => {
    return (anyObject as ITableColumnUrl).href !== undefined;
};
export const hasFormValuesTableColumn = (column: TableColumn): column is ITableColumnDict | ITableColumnAbook => {
    return (column as ITableColumnDict | ITableColumnAbook).formValues !== undefined;
};

export const getRowFormValues = (rowData: any, column: ITableColumnDict | ITableColumnAbook) => {
    if (!column.formValues?.formValue) return null;

    return column.formValues.formValue.reduce(
        (acc, formValue) => {
            acc[formValue.key] = rowData[formValue.attr];
            return acc;
        },
        {} as Record<string, any>,
    );
};

export const checkFormValuesConsistency = (
    keys: any[],
    grid: dxDataGrid<any, any>,
    column: ITableColumnDict | ITableColumnAbook,
): boolean => {
    const selectedRows = keys
        .map((key) => {
            const rowIndex = grid.getRowIndexByKey(key);
            return grid.getVisibleRows()[rowIndex]?.data;
        })
        .filter(Boolean);

    if (selectedRows.length > 1) {
        // Получаем formValues для первой строки как эталон
        const firstRowFormValues = getRowFormValues(selectedRows[0], column);

        // Находим все различающиеся значения
        const differentValues = new Set<string>();

        selectedRows.forEach((row) => {
            const currentRowFormValues = getRowFormValues(row, column);

            // Сравниваем каждый ключ
            if (firstRowFormValues && currentRowFormValues) {
                Object.keys(firstRowFormValues).forEach((key) => {
                    if (JSON.stringify(firstRowFormValues[key]) !== JSON.stringify(currentRowFormValues[key])) {
                        differentValues.add(
                            column.formValues.formValue.find((fv) => fv.key === key)?.displayName ?? key,
                        );
                    }
                });
            }
        });

        if (differentValues.size > 0) {
            const differentValuesList = Array.from(differentValues).join(', ');
            sendNotification({
                message: `У выделенных строк различаются следующие значения: ${differentValuesList}`,
                variant: 'red',
            });
            return false;
        }
    }

    return true;
};

export const hasChildWithClass = (baseElement: Element, className: string[]) => {
    let element: Element = baseElement;
    if (element.classList && className.some((x) => element.classList.contains(x))) {
        return true;
    }

    for (let child of element.children) {
        if (hasChildWithClass(child, className)) {
            return true;
        }
    }

    return false;
};

export const isVisibleRowButton = async (rules: string, rowData: any) => {
    if (rules) {
        let formulaMgr = new FormulaManager(rules);
        let formulaData: Record<string, IFieldElem> = {} as Record<string, IFieldElem>;
        for (let key in rowData) {
            formulaData[key] = {
                index: 0,
                name: '',
                value: rowData[key],
            };
        }
        formulaMgr.Init(formulaData);
        const result = await formulaMgr.EvalFormulaValues(true, false);
        return result as boolean;
    } else {
        return true;
    }
};

export const getDisplayWatch = (displayFormat: string) => {
    const regex = /{(?<watch>\|.*?)\}/gm;
    let result: string[] = [];
    let m;
    while ((m = regex.exec(displayFormat)) !== null) {
        if (m.index === regex.lastIndex) {
            regex.lastIndex++;
        }
        m.forEach((match, groupIndex) => {
            result.push(match);
        });
    }
    return result;
};

export const removeRows = (items: any, rowsData: any) => {
    rowsData.forEach((row: any) => {
        const index = items.indexOf(row);

        if (index >= 0) {
            items.splice(index, 1);
        }
    });
};

export const addRowsToIdx = (items: any, rowsData: any, currIndexRow: any, toIndex: number) => {
    if (toIndex >= 0) {
        toIndex = currIndexRow ? items.indexOf(currIndexRow) + 1 : toIndex;
        items.splice(toIndex, 0, ...rowsData);
    }
};

export const cellTextValueSwitcher = (p: any, column: any) => {
    let result = '';
    if (isTableColumnDict(column) || isTableColumnAbook(column)) {
        result = parseDisplayFormatCell(column.displayFormat, p.data);
    } else if (isTableColumnCalc(column) || isTableColumnAutoComplete(column) || isTableColumn(column)) {
        result = p.value;
    }

    if (column.valueType === 'date') {
        return new Date(result).toLocaleDateString('ru-RU');
    }

    if (column.valueType === 'datetime') {
        return new Date(result).toLocaleString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    }

    return result?.toString() ?? '';
};

export const findColumnByKey = (table: IDocumentTable, key: string): TableColumn | undefined => {
    const allColumns: TableColumn[] = [
        ...(table.tableColumn ?? []),
        ...(table.tableColumnDict ?? []),
        ...(table.tableColumnAbook ?? []),
        ...(table.tableColumnCalc ?? []),
        ...(table.tableColumnUrl ?? []),
        ...(table.tableColumnAutoComplete ?? []),
        ...(table.tableColumnExDataSource ?? []),
    ];
    return allColumns.find((c) => c.key === key);
};
